import { Box, Container, Stack, useTheme } from "@mui/material";
import { Button, FoodsIcon, FoodsSelect, Typography, WarningBox, WhiteBox } from "@foodpilot/foods";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { getApiUrl } from "src/api/axiosClient/utils";
import { useUpsertAppParam } from "src/api/client-api/appParams";
import { appParamCacheKey, useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { useDeleteAppParam } from "src/api/client-api/appParams/useDeleteAppParam";
import { useUpdateFile } from "src/api/client-api/appParams/useUpsertAppParam";
import { useSsoConnectors } from "src/api/client-api/sso/useSsoConnectors";
import { resolveSsoLoginMode, SSO_LOGIN_MODES, SsoLoginMode } from "src/pages/Authentication/ssoConnector";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import { useSnackbar } from "src/utils/useSnackbar";
import { useNavigateSettingsParent } from "../useNavigateSettingsParent";
import { Field, Section } from "./components/SsoSettingsFields";
import { SsoProvidersSection } from "./components/SsoProvidersSection";
import { AppParamPorts, LOGIN_MODE_KEY, persistLoginMode } from "./ssoSettingsDraft";

type ModeOption = { id: SsoLoginMode; label: string; description: string };

export const SingleSignOnSettings = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { isMobile } = useResponsiveBreakpoints();
    const navigateSettingsParent = useNavigateSettingsParent();
    const snackbar = useSnackbar();
    const { applicationParameters, fullApplicationParameters } = useApplicationParameters();

    const storedModeValue = applicationParameters?.[LOGIN_MODE_KEY];
    const storedMode = resolveSsoLoginMode(storedModeValue);

    const [mode, setMode] = useState<SsoLoginMode>(storedMode);
    const [isSaving, setSaving] = useState(false);

    const upsertAppParam = useUpsertAppParam({ forceReload: false });
    const deleteAppParam = useDeleteAppParam({ forceReload: false });
    const updateFile = useUpdateFile({ forceReload: false });
    const queryClient = useQueryClient();
    const apiOrigin = getApiUrl();

    const parameters = fullApplicationParameters ?? {};
    const { connectors } = useSsoConnectors();

    const ports: AppParamPorts = {
        upsert: ({ key, isNew, value }) => upsertAppParam.mutateAsync({ isNew, key, value }),
        remove: ({ key }) => deleteAppParam.mutateAsync({ key }),
        uploadLogo: ({ key, file }) => updateFile.mutateAsync({ key, value: file }),
        refetch: () => queryClient.refetchQueries([appParamCacheKey]),
    };

    const modeLabels: Record<SsoLoginMode, Omit<ModeOption, "id">> = {
        standard: {
            label: t("settings.sso.mode.standard", "Standard"),
            description: t("settings.sso.mode.standard_description", "Email and password first, connector below"),
        },
        "sso-first": {
            label: t("settings.sso.mode.sso_first", "SSO first"),
            description: t(
                "settings.sso.mode.sso_first_description",
                "Connector first, the password form moves behind a link",
            ),
        },
    };
    const modeOptions: ModeOption[] = SSO_LOGIN_MODES.map((id) => ({ id, ...modeLabels[id] }));
    const selectedMode = modeOptions.find((option) => option.id === mode) ?? null;
    const hasModeChange = mode !== storedMode;

    const save = async () => {
        setSaving(true);

        const saved = await persistLoginMode(mode, storedModeValue, ports);

        snackbar.setSnackbarProps(
            saved ?
                { type: "success", message: t("settings.sso.saved", "Single sign-on saved") }
            :   { type: "error", message: t("common.error_occurred") },
        );
        setSaving(false);
    };

    return (
        <Container
            component="main"
            maxWidth={false}
            sx={{
                margin: 0,
                padding: `${isMobile ? theme.spacing(2) : theme.spacing(6)} !important`,
                flexGrow: 1,
                overflow: "auto",
            }}
        >
            <Stack gap={theme.spacing(3)}>
                <WhiteBox
                    boxShadow="none"
                    borderRadius={theme.spacing(2)}
                    sx={{
                        border: `1px solid ${theme.custom.grey[500]}`,
                        overflow: "hidden",
                        backgroundColor: theme.custom.grey[100],
                    }}
                >
                    <Stack
                        flexDirection="row"
                        alignItems="center"
                        gap={theme.spacing(2)}
                        sx={{
                            padding: `${theme.spacing(3.5)} ${theme.spacing(4)}`,
                            borderBottom: `1px solid ${theme.custom.grey[500]}`,
                        }}
                    >
                        <Box
                            component="button"
                            type="button"
                            aria-label={t("common.back")}
                            onClick={navigateSettingsParent}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: theme.spacing(5),
                                height: theme.spacing(5),
                                flexShrink: 0,
                                border: "none",
                                borderRadius: theme.spacing(0.5),
                                backgroundColor: "transparent",
                                cursor: "pointer",
                                "&:hover": { backgroundColor: theme.custom.grey[300] },
                            }}
                        >
                            <FoodsIcon icon="arrowLeftShort" size={6} />
                        </Box>
                        <Typography variant="h3" component="h2">
                            {t("settings.platform.access_permissions.single_sign_on.title", "Single sign-on")}
                        </Typography>
                    </Stack>

                    <Stack gap={theme.spacing(4)} sx={{ padding: isMobile ? theme.spacing(2) : theme.spacing(4) }}>
                        <Section
                            title={t("settings.sso.login_page", "Login page")}
                            description={t(
                                "settings.sso.login_page_description",
                                "How the sign-in screen is laid out for everyone.",
                            )}
                        >
                            <Field label={t("settings.sso.mode", "Login mode")}>
                                <FoodsSelect
                                    selectedOption={selectedMode}
                                    options={modeOptions}
                                    onChange={(option) => option !== null && setMode(option.id)}
                                    getId={(option) => option.id}
                                    getName={(option) => option.label}
                                    textOptions={{
                                        placeholder: t("settings.sso.mode", "Login mode"),
                                        searchPlaceholder: t("component.select.searchplaceholder"),
                                        emptyOptionsText: t("component.select.nooptions"),
                                        defaultValueText: t("component.select.defaultvalue"),
                                        noResult: t("component.select.noresultfound"),
                                    }}
                                />
                                <Typography variant="small-medium" sx={{ color: theme.custom.grey[2000] }}>
                                    {selectedMode?.description}
                                </Typography>
                            </Field>
                        </Section>

                        <SsoProvidersSection
                            apiOrigin={apiOrigin}
                            loginMode={storedMode}
                            parameters={parameters}
                            ports={ports}
                        />
                    </Stack>

                    <Stack
                        flexDirection="row"
                        justifyContent="flex-end"
                        gap={theme.spacing(1)}
                        flexWrap="wrap"
                        sx={{
                            padding: `${theme.spacing(2.5)} ${theme.spacing(4)}`,
                            borderTop: `1px solid ${theme.custom.grey[500]}`,
                        }}
                    >
                        <Button
                            variant="ternary"
                            disabled={!hasModeChange || isSaving}
                            onClick={() => setMode(storedMode)}
                        >
                            {t("common.cancel")}
                        </Button>
                        <Button variant="primary" disabled={!hasModeChange || isSaving} onClick={() => void save()}>
                            {t("common.save")}
                        </Button>
                    </Stack>
                </WhiteBox>

                {storedMode === "sso-first" && connectors.length === 0 && (
                    <WarningBox>
                        <Typography variant="body-medium">
                            {t(
                                "settings.sso.missing_connector_warning",
                                "The “SSO first” mode is active but no valid connector is configured: the login page will look unchanged and SSO members will have no way in.",
                            )}
                        </Typography>
                    </WarningBox>
                )}
            </Stack>
        </Container>
    );
};
